<template>
	<div
		v-if="dataCount > dataPerPage"
		class="pagination"
		:class="{ 'pagination--small': $props.small }"
	>
		<div class="result">
			<span class="heading-5" v-html="pagesResultMessage"></span>
		</div>
		<div class="pages">
			<ja-arrow-button
				name="previous-page"
				class="previous-page"
				:disabled="currentPage === 1"
				:direction="'left'"
				@click.native="selectPage(currentPage - 1)"
			></ja-arrow-button>
			<span
				v-for="page in pagesCount"
				v-show="showPageButton(page)"
				:key="page"
				class="body-text-1 page-button"
				:class="{ 'selected-page': page === currentPage }"
				@click="selectPage(page)"
			>
				<span v-if="showTripleDot(page, 'prev')">...</span>
				{{ page }}
				<span v-if="showTripleDot(page, 'next')">...</span>
			</span>
			<ja-arrow-button
				name="next-page"
				class="next-page"
				:disabled="isLastPage"
				:direction="'right'"
				@click.native="selectPage(currentPage + 1)"
			></ja-arrow-button>
		</div>
	</div>
</template>
<script>
import ArrowButton from '../../ui/ArrowButton';
export default {
	components: {
		jaArrowButton: ArrowButton
	},
	props: {
		dataPerPage: {
			type: Number,
			default: 1
		},
		dataCount: {
			type: Number,
			default: 1
		},
		small: {
			type: Boolean,
			default: false
		},
		current: {
			type: Number,
			default: 1
		}
	},
	data() {
		return {
			currentPage: 1,
			maxVisibleRange: this.small ? 1 : 2, // Defines visible elements surrounded the currently selected page
			extendRange: this.small ? 1 : 0
		};
	},
	computed: {
		pagesResultMessage() {
			const message = this.$t('pageResults', {
				firstOnDisplay: this.firstOnDisplay,
				lastOnDisplay: this.lastOnDisplay,
				total: this.dataCount
			}).split(':');

			return `<span class="display-block">${message[0]}:</span><span class="display-block">${message[1]}</span>`;
		},
		firstOnDisplay() {
			return (this.currentPage - 1) * this.dataPerPage + 1;
		},
		lastOnDisplay() {
			if (this.isLastPage) {
				return this.dataCount;
			}
			return (this.currentPage - 1) * this.dataPerPage + this.dataPerPage;
		},
		pagesCount() {
			return Math.ceil(this.dataCount / this.dataPerPage);
		},
		isLastPage() {
			return this.currentPage === this.pagesCount;
		}
	},
	watch: {
		dataCount() {
			if (this.currentPage > this.pagesCount) {
				this.currentPage = this.pagesCount;
				this.$emit('changePage', this.currentPage);
			}
		},
		current() {
			this.currentPage =
				this.current && this.current > 0 ? this.current : this.currentPage;
		}
	},
	mounted() {
		this.currentPage = this.current && this.current > 0 ? this.current : 1;
	},
	methods: {
		showPageButton(page) {
			// used to check if page is in a specific range
			// ex: page === this.currentPage + 2 || page === this.currentPage - 2
			const maxVisibleElements = Math.abs(page - this.currentPage);

			return (
				page === 1 ||
				page === this.pagesCount ||
				page === this.currentPage ||
				maxVisibleElements <= this.maxVisibleRange
			);
		},
		selectPage(page) {
			if (page >= 1 && page <= this.pagesCount) {
				this.currentPage = page;
				this.$emit('changePage', page);
			}
		},
		showTripleDot(page, direction) {
			if (direction === 'prev') {
				return (
					page === this.currentPage - this.maxVisibleRange &&
					this.currentPage - this.maxVisibleRange >
						this.maxVisibleRange + this.extendRange
				);
			}

			return (
				page === this.currentPage + this.maxVisibleRange &&
				this.currentPage + this.maxVisibleRange < this.pagesCount - 1
			);
		}
	}
};
</script>
<style lang="scss" scoped>
.pagination {
	font-family: 'RobotoRegular';
	font-size: 16px;
	letter-spacing: 0.5px;
	line-height: 23px;
	height: 65px;
	display: flex;
	align-items: center;
	justify-content: flex-end;

	.result {
		margin-left: 16px;
		flex-grow: 1;
	}

	h5 {
		color: $font-dark;
	}

	.pages {
		margin-right: 16px;
		.page-button {
			color: $font-blue;
			margin: 0px 4px 0px 4px;
			vertical-align: middle;
			&:hover {
				cursor: pointer;
				font-weight: 600;
			}

			@media (min-width: $breakpoint-mid) {
				margin: 0px 6px 0px 6px;
			}
		}

		.selected-page {
			color: $disabled-pagination-button-color;
			&:hover {
				cursor: default;
				font-weight: 500;
			}
		}

		.next-page,
		.previous-page {
			&:disabled {
				::v-deep svg {
					fill: $disabled-pagination-button-color;
				}
			}
		}
	}

	.previous-page {
		margin-right: 4px;

		@media (min-width: $breakpoint-mid) {
			margin-right: 6px;
		}
	}

	.next-page {
		margin-left: 4px;

		@media (min-width: $breakpoint-mid) {
			margin-left: 6px;
		}
	}

	&--small {
		width: 100%;
		justify-content: space-between;

		.result {
			flex-grow: 0;
			flex-basis: 40%;
		}

		::v-deep.display-block {
			display: inline-block;
		}

		.pages {
			.page-button {
				@media (min-width: $breakpoint-mid) {
					margin: 0px 2px;
					font-size: 15px;
					font-weight: bold;
				}
			}
		}

		.previous-page {
			@media (min-width: $breakpoint-mid) {
				margin-right: 0px;
			}
		}

		.next-page {
			@media (min-width: $breakpoint-mid) {
				margin-left: 0px;
			}
		}
	}
}
</style>
